import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";

const STEPS = [
  { n: 1, code: "1", label: "Working Well (Self)", phase: "PEOPLE" },
  { n: 2, code: "2", label: "Do Differently (Self)", phase: "PEOPLE" },
  { n: 3, code: "3", label: "Do Differently (Coach)", phase: "PEOPLE" },
  { n: 4, code: "4", label: "Doing Well (Coach)", phase: "PEOPLE" },
];
const LAST_STEP = STEPS.length;

const labelCls = "block text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1.5";
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent";
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent";

const empty = { subject: "", role: "", selfWell: "", selfDifferently: "", coachDifferently: "", coachWell: "", actionPlan: "", owner: "", reviewDate: "" };
const FIELD_BY_STEP = { 1: "selfWell", 2: "selfDifferently", 3: "coachDifferently", 4: "coachWell" };

export default function CoachingNote() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [data, setData] = useState(empty);
  const [coPilotOpen, setCoPilotOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/documents/${id}`);
        setTitle(res.data.title || "");
        setData({ ...empty, ...(res.data.payload || {}) });
      } catch {
        toast.error("Document not found");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try {
      await api.put(`/documents/${id}`, { title: title || "Untitled Coaching Note", payload: data, ...extra });
      return true;
    } catch {
      toast.error("Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  }, [id, title, data]);

  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST_STEP, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => {
    if (await persist({ status: "Completed" })) {
      toast.success("Coaching note finalised");
      navigate(`/coaching/${id}/view`);
    }
  };

  const applySuggestion = (text) => {
    const field = FIELD_BY_STEP[step];
    setData((d) => {
      const existing = d[field];
      const merged = !existing || !existing.trim() ? text : `${existing.trimEnd()}\n- ${text}`;
      return { ...d, [field]: merged };
    });
    toast.success("Suggestion added to this step");
  };

  if (loading)
    return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3">
          <ArrowLeft size={16} /> Workspace
        </button>
        <input data-testid="coaching-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Coaching note title…"
          className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />

        <div className="mt-5 flex items-center gap-2" data-testid="coaching-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)}
              className={`flex-1 rounded-sm border px-3 py-2.5 text-left transition-all ${step === s.n ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold ${step === s.n ? "text-emerald-700" : "text-slate-400"}`}>STEP {s.n}</span>
              <p className={`text-sm font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">What do you think is working well?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Recipient</label>
                  <input data-testid="field-subject" value={data.subject} onChange={(e) => set({ subject: e.target.value })} placeholder="Who is this feedback for?" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Role</label>
                  <input data-testid="field-role" value={data.role} onChange={(e) => set({ role: e.target.value })} placeholder="Their role / team" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Their view — what's working well</label>
                <textarea data-testid="field-selfWell" rows={6} className={taCls} value={data.selfWell} onChange={(e) => set({ selfWell: e.target.value })} placeholder="Capture the recipient's own reflection on what they feel is going well. Let them speak first." />
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">What do you think you could do differently?</h2>
              <div>
                <label className={labelCls}>Their view — what they'd do differently</label>
                <textarea data-testid="field-selfDifferently" rows={7} className={taCls} value={data.selfDifferently} onChange={(e) => set({ selfDifferently: e.target.value })} placeholder="Capture the recipient's own view of what they could change or improve — behaviours within their control." />
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">What I think you could do differently</h2>
              <div>
                <label className={labelCls}>Your input — what they could do differently</label>
                <textarea data-testid="field-coachDifferently" rows={7} className={taCls} value={data.coachDifferently} onChange={(e) => set({ coachDifferently: e.target.value })} placeholder="Your developmental input — specific, observable, behaviour-focused. Offer it as a contribution, not a verdict." />
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">What I think you are doing well</h2>
              <div>
                <label className={labelCls}>Your input — what they're doing well</label>
                <textarea data-testid="field-coachWell" rows={5} className={taCls} value={data.coachWell} onChange={(e) => set({ coachWell: e.target.value })} placeholder="Reinforce, sincerely and specifically, what they are genuinely doing well. End on strengths." />
              </div>
              <div className="pt-2 border-t border-slate-100">
                <label className={labelCls}>Agreed action plan</label>
                <textarea data-testid="field-actionPlan" rows={4} className={taCls} value={data.actionPlan} onChange={(e) => set({ actionPlan: e.target.value })} placeholder="What did you both agree to? Capture the two-way commitment and concrete next steps." />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Accountable Owner</label>
                  <input data-testid="field-owner" value={data.owner} onChange={(e) => set({ owner: e.target.value })} placeholder="Who owns the follow-through?" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Review Date</label>
                  <input data-testid="field-reviewDate" type="date" value={data.reviewDate} onChange={(e) => set({ reviewDate: e.target.value })} className={inputCls} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="coaching-back" onClick={back} disabled={step === 1}
            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="coaching-save-draft" onClick={saveDraft} disabled={saving}
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft
            </button>
            {step < LAST_STEP ? (
              <button data-testid="coaching-next" onClick={next}
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700">
                Next Step <ArrowRight size={16} />
              </button>
            ) : (
              <button data-testid="coaching-finalise" onClick={finalise} disabled={saving}
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700">
                <CheckCircle size={18} weight="fill" /> Finalise Note
              </button>
            )}
          </div>
        </div>
      </div>

      <CoPilotToggle open={coPilotOpen} onClick={() => setCoPilotOpen(true)} phase={STEPS[step - 1].phase} />
      <CoPilotPanel open={coPilotOpen} onClose={() => setCoPilotOpen(false)} docId={id} toolType="coaching" step={STEPS[step - 1]} phase={STEPS[step - 1].phase} onApplySuggestion={applySuggestion} />
    </Layout>
  );
}
